import { motion, MotionConfig } from "framer-motion";
import {
  GraduationCap,
  LayoutDashboard,
  ClipboardList,
  Brain,
  DollarSign,
  Network,
} from "lucide-react";
import { useInViewOnce } from "../../../hooks/useInViewOnce";

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const MODULES = [
  {
    icon: GraduationCap,
    gradient: "from-teal-400 to-cyan-500",
    glow: "shadow-teal-500/20",
    title: "Gestão Acadêmica",
    description:
      "Turmas, disciplinas, matrículas, calendário e cronogramas centralizados. Coordenação e diretoria com visão consolidada.",
    tag: "Core",
  },
  {
    icon: ClipboardList,
    gradient: "from-emerald-400 to-teal-500",
    glow: "shadow-emerald-500/20",
    title: "Avaliações e Notas",
    description:
      "Atividades objetivas e discursivas com correção assistida. Camada de notas separada da gamificação — institucional tem peso, trilhas não.",
    tag: "Core",
  },
  {
    icon: LayoutDashboard,
    gradient: "from-violet-400 to-fuchsia-500",
    glow: "shadow-violet-500/20",
    title: "Painel do Coordenador",
    description:
      "Painéis por turma, disciplina e professor. Indicadores de risco, engajamento e desempenho — decisão com dados, não com feeling.",
    tag: "Gestão",
  },
  {
    icon: Brain,
    gradient: "from-cyan-400 to-blue-500",
    glow: "shadow-cyan-500/20",
    title: "IA Pedagógica",
    description:
      "Plano de estudo adaptativo, revisão com espaçamento, simulados e explicações personalizadas por perfil VARK.",
    tag: "IA",
  },
  {
    icon: DollarSign,
    gradient: "from-amber-400 to-orange-500",
    glow: "shadow-amber-500/20",
    title: "Módulo Financeiro",
    description:
      "Matrícula, mensalidades, status de pagamento e relatórios financeiros. Família acompanha pelo painel sem ligar para a secretaria.",
    tag: "Roadmap",
  },
  {
    icon: Network,
    gradient: "from-rose-400 to-pink-500",
    glow: "shadow-rose-500/20",
    title: "Mapa de Conhecimento",
    description:
      "Visualização dos conceitos dominados e das lacunas de cada aluno. Professores veem pontos críticos antes da prova.",
    tag: "IA",
  },
];

const TAG_STYLE: Record<string, string> = {
  Core: "border-teal-400/40 bg-teal-400/10 text-teal-300",
  Gestão: "border-violet-400/40 bg-violet-400/10 text-violet-300",
  IA: "border-cyan-400/40 bg-cyan-400/10 text-cyan-300",
  Roadmap: "border-amber-400/40 bg-amber-400/10 text-amber-300",
};

const cardVariants = {
  hidden: { opacity: 0, y: 36 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: SPRING, delay: i * 0.07 },
  }),
};

export function ModulesSection() {
  const { ref, isVisible } = useInViewOnce();

  return (
    <MotionConfig reducedMotion="user">
      <section
        ref={ref}
        className="border-b border-white/[0.06] py-16 sm:py-20 lg:py-24"
      >
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0, 0, 0.2, 1] }}
            className="mx-auto max-w-2xl text-center"
          >
            <span className="mb-4 inline-block rounded-full border border-teal-400/30 bg-teal-400/10 px-3 py-1 text-xs font-medium text-teal-300 sm:text-sm">
              Plataforma completa
            </span>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl">
              Tudo que uma escola precisa{" "}
              <span className="bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent">
                em um só lugar
              </span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
              Seis módulos integrados que cobrem desde a matrícula até o mapa de
              conhecimento do aluno. Core já disponível; Financeiro e Mapa em roadmap.
            </p>
          </motion.div>

          <div className="mx-auto mt-12 grid max-w-6xl gap-5 sm:mt-16 sm:grid-cols-2 lg:grid-cols-3">
            {MODULES.map((mod, i) => (
              <motion.div
                key={mod.title}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                animate={isVisible ? "visible" : "hidden"}
                className="card-lift group flex flex-col gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-sm"
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg ${mod.gradient} ${mod.glow}`}
                  >
                    <mod.icon className="h-6 w-6 text-white" aria-hidden />
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${TAG_STYLE[mod.tag] ?? ""}`}
                  >
                    {mod.tag}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-white transition-colors group-hover:text-teal-300">
                    {mod.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    {mod.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </MotionConfig>
  );
}
